import React from 'react';
import { ActivityIndicator, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { WebView } from 'react-native-webview';
import { Ionicons } from '@expo/vector-icons';
import { InsetModal as Modal } from '../InsetModal';
import { colors, spacing, typography } from '../../theme';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';

interface Props {
  visible: boolean;
  html: string;
  loading: boolean;
  title: string;
  onClose: () => void;
  error?: string;
  onRetry?: () => void;
}

/** The close control stays outside the document's scrollable preview on every platform. */
export const DocumentPreviewModal: React.FC<Props> = ({ visible, html, loading, title, onClose, error, onRetry }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  return (
    <Modal visible={visible} animationType="slide" presentationStyle={Platform.OS === 'ios' ? 'pageSheet' : 'fullScreen'} onRequestClose={onClose}>
      <View style={styles.fill}>
        <View style={[styles.header, { flexDirection: rtl.rowDirection }]}>
          <View style={styles.titleWrap}>
            <Text style={[styles.title, { textAlign: rtl.textStart }]} numberOfLines={2}>{title}</Text>
            <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t('documents.editor.sampleData' as any)}</Text>
          </View>
          <TouchableOpacity style={[styles.close, { flexDirection: rtl.rowDirection }]} onPress={onClose} accessibilityLabel={t('common.close')}>
            <Ionicons name="close" size={22} color={colors.text.primary as string} />
            <Text style={styles.closeText}>{t('common.close')}</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.body}>
          {loading ? <View style={styles.center}><ActivityIndicator size="large" color={colors.primary as string} /></View>
            : error ? <View style={styles.center}>
              <Ionicons name="alert-circle-outline" size={32} color={colors.error as string} />
              <Text style={styles.error}>{error}</Text>
              {!!onRetry && <TouchableOpacity style={styles.retry} onPress={onRetry}><Text style={styles.retryText}>{t('common.retry')}</Text></TouchableOpacity>}
            </View>
              : Platform.OS === 'web'
                ? <iframe title={title} sandbox="" srcDoc={html} style={{ flex: 1, width: '100%', height: '100%', minHeight: 0, border: 'none', background: '#fff' }} />
                : <WebView source={{ html }} style={styles.webView} javaScriptEnabled={false} originWhitelist={['*']} />}
        </View>
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  fill: { flex: 1, backgroundColor: colors.background.base },
  header: { alignItems: 'center', gap: spacing.md, padding: spacing.md, borderBottomWidth: 1, borderBottomColor: colors.border, backgroundColor: colors.background.surface },
  titleWrap: { flex: 1, minWidth: 0 },
  title: { color: colors.text.primary, fontSize: typography.fontSize.base, fontWeight: '700' },
  hint: { color: colors.text.secondary, fontSize: typography.fontSize.xs, marginTop: 3 },
  close: { alignItems: 'center', gap: spacing.xs, minHeight: 44, paddingHorizontal: spacing.sm },
  closeText: { color: colors.text.primary, fontSize: typography.fontSize.sm, fontWeight: '600' },
  body: { flex: 1, minHeight: 0, backgroundColor: '#fff' },
  webView: { flex: 1, backgroundColor: '#fff' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: spacing.lg, gap: spacing.md },
  error: { color: colors.error, textAlign: 'center', fontSize: typography.fontSize.sm },
  retry: { paddingHorizontal: spacing.lg, paddingVertical: spacing.sm, backgroundColor: colors.primary, borderRadius: 10 },
  retryText: { color: colors.white, fontWeight: '700' },
});
